/**
 * SyncBadge — inline sync status indicator for admin tables.
 *
 * Props:
 *   status        "pending" | "synced" | "failed" | "conflict" | "skipped" | "dead"
 *   lastSyncedAt  ISO string (optional)
 *   syncError     string (optional) — shown in tooltip on hover
 *   onRetry       fn() — called when admin clicks Retry (failed/dead)
 *   onForceSync   fn() — called when admin clicks Force (pending)
 *   compact       boolean — hide label, icon only (for tight columns)
 */
import { useState } from "react";
import { RefreshCcw, CheckCircle2, XCircle, AlertTriangle, Clock, SkipForward, Zap } from "lucide-react";

const CONFIGS = {
  synced:   { icon: CheckCircle2, label: "Synced",   bg: "bg-green-500/15",   text: "text-green-400",   border: "border-green-500/30"  },
  pending:  { icon: Clock,        label: "Pending",  bg: "bg-yellow-500/10",  text: "text-yellow-300",  border: "border-yellow-500/25" },
  failed:   { icon: XCircle,      label: "Failed",   bg: "bg-red-500/15",     text: "text-[#ff6b6b]",   border: "border-red-500/30"    },
  dead:     { icon: XCircle,      label: "Dead",     bg: "bg-red-900/30",     text: "text-red-300",     border: "border-red-700/40"    },
  conflict: { icon: AlertTriangle,label: "Conflict", bg: "bg-amber-500/15",   text: "text-amber-300",   border: "border-amber-500/30"  },
  skipped:  { icon: SkipForward,  label: "Skipped",  bg: "bg-blue-500/10",    text: "text-blue-300",    border: "border-blue-500/20"   },
};

export default function SyncBadge({
  status = "pending",
  lastSyncedAt,
  syncError,
  onRetry,
  onForceSync,
  compact = false,
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [busy, setBusy] = useState(false);

  const cfg = CONFIGS[status] || CONFIGS.pending;
  const Icon = cfg.icon;

  const handleRetry = async (e) => {
    e.stopPropagation();
    if (!onRetry) return;
    setBusy(true);
    try { await onRetry(); } finally { setBusy(false); }
  };

  const handleForce = async (e) => {
    e.stopPropagation();
    if (!onForceSync) return;
    setBusy(true);
    try { await onForceSync(); } finally { setBusy(false); }
  };

  const canRetry  = (status === "failed" || status === "dead" || status === "conflict") && onRetry;
  const canForce  = status === "pending" && onForceSync;

  return (
    <div className="flex items-center gap-1.5">
      {/* Badge */}
      <div
        className="relative"
        onMouseEnter={() => (syncError || lastSyncedAt) && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span
          className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest
            px-1.5 py-0.5 rounded-sm border cursor-default select-none
            ${cfg.bg} ${cfg.text} ${cfg.border}`}
        >
          <Icon className="h-2.5 w-2.5 shrink-0" />
          {!compact && cfg.label}
        </span>

        {/* Tooltip */}
        {showTooltip && (syncError || lastSyncedAt) && (
          <div
            className="absolute z-50 bottom-full left-0 mb-1.5 w-64 p-2.5 rounded-sm
              bg-[#0b1a3b] border border-white/15 shadow-xl pointer-events-none"
          >
            {lastSyncedAt && (
              <div className="text-[10px] text-blue-300 font-mono mb-1">
                Last sync: {new Date(lastSyncedAt).toLocaleString("en-AU")}
              </div>
            )}
            {syncError && (
              <div className="text-[10px] text-[#ff6b6b] leading-relaxed break-words">
                {syncError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      {canRetry && (
        <button
          onClick={handleRetry}
          disabled={busy}
          title="Retry sync"
          className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-widest
            px-1.5 py-0.5 rounded-sm border border-blue-500/30 bg-blue-500/10
            text-blue-300 hover:bg-blue-500/20 hover:text-blue-100 transition-colors disabled:opacity-40"
        >
          <RefreshCcw className={`h-2.5 w-2.5 ${busy ? "animate-spin" : ""}`} />
          {!compact && "Retry"}
        </button>
      )}

      {canForce && (
        <button
          onClick={handleForce}
          disabled={busy}
          title="Force sync now"
          className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-widest
            px-1.5 py-0.5 rounded-sm border border-yellow-500/30 bg-yellow-500/10
            text-yellow-300 hover:bg-yellow-500/20 hover:text-yellow-100 transition-colors disabled:opacity-40"
        >
          <Zap className={`h-2.5 w-2.5 ${busy ? "animate-pulse" : ""}`} />
          {!compact && "Force"}
        </button>
      )}
    </div>
  );
}
